import { NormalizedFMSLeadInterface } from "../Leads/Lead.Interface";
import { LeaseAccountPaymentStatus, LeaseAccountStatus, LeaseInterface, NormalizedFMSLeaseInterface } from "../Lease/Lease.Interface";
import { NormalizedFMSTenantInterface, TenantInterface } from "../Tenant/Tenant.Interface";
import { NormalizedFMSUnitInterface, UnitInterface } from "../Unit/Unit.Interface";
import { FacilityAndSpaceFeatures } from "../Variables/Cortex/CortexFacilityFeatures.Data";
import { VarKey } from "../Variables/Generated/VarKey.Data";
import { FMSPaymentInterface, NormalizedMakePaymentResponse } from "./FMS.Payments.Interface";
import { FMSValidAuth } from "./FMSAuthentication.Model";

export enum FMSType {
  SiteLink = "SiteLink",
  SSM = "SSM",
  SSMCloud = "SSMCloud",
  StorEdge = "StorEdge",
  Tenant = "Tenant",
  StorageMart = "StorageMart",
  DoorSwap = "DoorSwap",
  StorageCommander = "StorageCommander",
  Storage360 = "Storage360",
  Cubby = "Cubby",
  QuikStor = "QuikStor",
  Monument = "Monument",
  Yardi = "Yardi"
}

export interface FacilityInterface {
  [VarKey.LocationFMSId]: string;
  [VarKey.LocationLocationNumber]: string;
  [VarKey.LocationFMSType]?: string;
  [VarKey.LocationName]?: string;
  [VarKey.LocationFMSIntegrationId]?: string;
  [VarKey.LocationBrandId]?: string;
}

export interface StoreInfo {
  // This should be depricated
  LocationName: string;
  LocationAddress: string;
  LocationPhone: string;
  LocationOfficeHours: string;
  LocationAccessHours: string;
  LocationEmail: string;
}

export enum SpaceType {
  STORAGE_UNIT = "storage unit",
  PARKING = "parking",
  RV_PARKING = "rv parking"
}

// export enum SpaceFeatureName { // This is deprecated!
//   IsParking = 'IsParking',
//   Indoor = 'Indoor',
//   Power = 'Power',
//   Alarm = 'Alarm',
//   ClimateControl = 'ClimateControl',
//   FirstFloor = 'FirstFloor',
//   DriveUp = 'DriveUp',
//   UpperLevel = 'UpperLevel',
//   ElevatorAccess = 'ElevatorAccess'
// }
// `SpaceFeature` was folded into `FacilityAndSpaceFeatures` (ENG-3209) — same string values,
// one vocabulary for units and facilities. The legacy display map and tag list below are
// kept for the legacy chat/messaging space picker only; Snowy surfaces name features through
// `featureDisplayName` and the operator's Cortex override.
export const SpaceTags: readonly FacilityAndSpaceFeatures[] = [
  // These are things we display as tags
  FacilityAndSpaceFeatures.ClimateControl,
  FacilityAndSpaceFeatures.BoatParking,
  FacilityAndSpaceFeatures.Locker,
  FacilityAndSpaceFeatures.CommercialWarehouse,
  FacilityAndSpaceFeatures.OfficeSpace,
  FacilityAndSpaceFeatures.FirstFloor,
  FacilityAndSpaceFeatures.Unpaved,
  FacilityAndSpaceFeatures.Paved,
  FacilityAndSpaceFeatures.Covered,
  FacilityAndSpaceFeatures.Enclosed,
  FacilityAndSpaceFeatures.PullThru,
  FacilityAndSpaceFeatures.Heated,
  FacilityAndSpaceFeatures.UpperLevel,
  FacilityAndSpaceFeatures.Inside,
  FacilityAndSpaceFeatures.DriveUp,
  FacilityAndSpaceFeatures.Elevators,
  FacilityAndSpaceFeatures.Alarm,
  FacilityAndSpaceFeatures.Power,
  FacilityAndSpaceFeatures.WineStorage,
  FacilityAndSpaceFeatures.SafetyDepositBox,
  FacilityAndSpaceFeatures.RinseAndDumpStation,
  FacilityAndSpaceFeatures.RinseStation,
  FacilityAndSpaceFeatures.AirAndVacuumStation
];

export const DefaultSpacePickerFeatureDisplayNames: Partial<Record<FacilityAndSpaceFeatures, string>> = {
  // [SpaceFeatureName.IsParking]: 'Parking',
  // [SpaceFeatureName.Indoor]: 'Indoor',
  // [SpaceFeatureName.Power]: 'Power',
  // [SpaceFeatureName.Alarm]: 'Alarm',
  // [SpaceFeatureName.ClimateControl]: 'Climate Control',
  // [SpaceFeatureName.FirstFloor]: 'First Floor',
  // [SpaceFeatureName.DriveUp]: 'Drive Up',
  // [SpaceFeatureName.UpperLevel]: 'Upper Level',
  // [SpaceFeatureName.ElevatorAccess]: 'Elevator Access',
  [FacilityAndSpaceFeatures.OfficeSpace]: "Office Space",
  [FacilityAndSpaceFeatures.CommercialWarehouse]: "Commercial Warehouse",
  [FacilityAndSpaceFeatures.BoatParking]: "Boat Parking",
  [FacilityAndSpaceFeatures.Locker]: "Locker",
  [FacilityAndSpaceFeatures.StorageUnit]: "Storage Unit",
  [FacilityAndSpaceFeatures.Parking]: "Parking",
  [FacilityAndSpaceFeatures.RVParking]: "RV Parking",
  [FacilityAndSpaceFeatures.ClimateControl]: "Climate Control",
  [FacilityAndSpaceFeatures.FirstFloor]: "First Floor",
  [FacilityAndSpaceFeatures.Unpaved]: "Unpaved",
  [FacilityAndSpaceFeatures.Paved]: "Paved",
  [FacilityAndSpaceFeatures.Covered]: "Covered",
  [FacilityAndSpaceFeatures.Enclosed]: "Enclosed",
  [FacilityAndSpaceFeatures.PullThru]: "Pull Thru",
  [FacilityAndSpaceFeatures.Heated]: "Heated",
  [FacilityAndSpaceFeatures.UpperLevel]: "Upper Level",
  [FacilityAndSpaceFeatures.Inside]: "Inside",
  [FacilityAndSpaceFeatures.DriveUp]: "Drive Up",
  [FacilityAndSpaceFeatures.Elevators]: "Elevator Access",
  [FacilityAndSpaceFeatures.Alarm]: "Alarm",
  [FacilityAndSpaceFeatures.Power]: "Power",
  [FacilityAndSpaceFeatures.WineStorage]: "Wine Storage",
  [FacilityAndSpaceFeatures.GunStorage]: "Gun Storage",
  [FacilityAndSpaceFeatures.SafetyDepositBox]: "Safety Deposit Box",
  [FacilityAndSpaceFeatures.RinseAndDumpStation]: "Rinse and Dump Station",
  [FacilityAndSpaceFeatures.RinseStation]: "Rinse Station",
  [FacilityAndSpaceFeatures.AirAndVacuumStation]: "Air and Vacuum Station"
};

export interface SpaceInterface {
  id: string;
  type: SpaceType;
  name: string;
  dimensions?: string;
  unitRate?: string;
  width?: number;
  length?: number;
  imageURL?: string;
  costLow?: string;
  costHigh?: string;
  isParking?: boolean;
  hasClimateControl: boolean;
  isInside: boolean;
  hasPower: boolean;
  hasAlarm: boolean;
  floor: number;

  isUpperLevel?: boolean;
  isHeated?: boolean;
  isDriveUp?: boolean;
  hasElevatorAccess?: boolean;

  features?: FacilityAndSpaceFeatures[];
}
// export function SpaceInterfaceToSpaceVariables(_space: SpaceInterface): { [key: string]: string } {
//   const space: any = { ..._space } || {}
//   for (const key in space) {
//     if (Object.prototype.hasOwnProperty.call(space, key)) {
//       space[key] = String(space[key])
//     }
//   }

//   return {
//     [SpaceVars.SpaceId]: space.id,
//     [SpaceVars.SpaceName]: space.name || '',
//     [SpaceVars.SpaceDimensions]: space.dimensions || '',
//     [SpaceVars.SpaceRate]: space.costLow || space.costHigh || '',
//     [SpaceVars.SpaceHasClimateControl]:
//       space.hasClimateControl || space.features.includes(FacilityAndSpaceFeatures.ClimateControl) ? '1' : '0',
//     [SpaceVars.SpaceIsInside]:
//       space.isInside || space.features.includes(FacilityAndSpaceFeatures.Inside) ? '1' : '0',
//     [SpaceVars.SpaceHasPower]:
//       space.hasPower || space.features.includes(FacilityAndSpaceFeatures.Power) ? '1' : '0',
//     [SpaceVars.SpaceFloor]: String(space.floor),
//     [SpaceVars.SpaceIsParking]:
//       space.isParking || space.features.includes(FacilityAndSpaceFeatures.Parking) ? '1' : '0',
//     [SpaceVars.SpaceIsDriveUp]:
//       space.isDriveUp || space.features.includes(FacilityAndSpaceFeatures.DriveUp) ? '1' : '0',
//     [SpaceVars.SpaceHasElevatorAccess]:
//       space.hasElevatorAccess || space.features.includes(FacilityAndSpaceFeatures.Elevators) ? '1' : '0',
//     [SpaceVars.SpaceHasAlarm]:
//       space.hasAlarm || space.features.includes(FacilityAndSpaceFeatures.Alarm) ? '1' : '0',
//     [SpaceVars.SpaceFeatures]:
//       space.features && space.features.constructor === Array ? space.features.join(',') : ''
//   }
// }
// export function SpaceVariablesToSpaceInterface(variables: {
//   [key: string]: string
// }): SpaceInterface {
//   const features = variables[SpaceVars.SpaceFeatures]
//     ? (variables[SpaceVars.SpaceFeatures].split(',') as FacilityAndSpaceFeatures[])
//     : []

//   return {
//     id: variables[SpaceVars.SpaceId] || '',
//     name: variables[SpaceVars.SpaceName] || '',
//     dimensions: variables[SpaceVars.SpaceDimensions] || '',
//     costLow: String(variables[SpaceVars.SpaceRate]) || '',
//     costHigh: String(variables[SpaceVars.SpaceRate]) || '',
//     hasClimateControl:
//       variables[SpaceVars.SpaceHasClimateControl] === '1' ||
//       features.includes(FacilityAndSpaceFeatures.ClimateControl),
//     isInside: variables[SpaceVars.SpaceIsInside] === '1' || features.includes(FacilityAndSpaceFeatures.Inside),
//     hasPower: variables[SpaceVars.SpaceHasPower] === '1' || features.includes(FacilityAndSpaceFeatures.Power),
//     floor: Number(variables[SpaceVars.SpaceFloor] || '0'),
//     isParking:
//       variables[SpaceVars.SpaceIsParking] === '1' || features.includes(FacilityAndSpaceFeatures.Parking),
//     isDriveUp:
//       variables[SpaceVars.SpaceIsDriveUp] === '1' || features.includes(FacilityAndSpaceFeatures.DriveUp),
//     hasElevatorAccess:
//       variables[SpaceVars.SpaceHasElevatorAccess] === '1' ||
//       features.includes(FacilityAndSpaceFeatures.Elevators),
//     hasAlarm: variables[SpaceVars.SpaceHasAlarm] === '1' || features.includes(FacilityAndSpaceFeatures.Alarm),
//     type: variables[SpaceVars.SpaceIsParking] === '1' ? SpaceType.PARKING : SpaceType.STORAGE_UNIT,
//     features: variables[SpaceVars.SpaceFeatures] ? features : []
//   }
// }

//Temporary until we rework SpaceInterface

// export interface NormalizedFMSTenantInterface {
//   id: string;
//   name: string;
//   firstName: string;
//   lastName: string;
//   email: string;
//   phone: string;
//   addressLine1: string;
//   addressLine2: string;
//   city: string;
//   state: string;
//   zip: string;
//   countryCode?: string;
//   active: boolean;
//   accountNumber?: number;
//   smsOptIn: boolean;
// }

export interface NormalizedUnitInterface {
  id: string;
  externalTenantId: string;
  type: SpaceType;
  name: string;
  dimensions: string;
  width: number;
  length: number;
  rate: number;
  available: boolean;
  floor: number;
  number: string;
  features: FacilityAndSpaceFeatures[];
}

export interface NormalizedLeaseInterface {
  id: string;
  active: boolean;
  externalUnitId: string;
  externalTenantId?: string;
  startDate: Date;
  endDate: Date;
  moveInDate: Date;
  moveOutDate: Date;
  rent: number;
  paidThroughDate: Date;
  balance: number;
  autopay: boolean;
  daysPastDue: number;
  accountStatus: LeaseAccountStatus;
  paymentStatus: LeaseAccountPaymentStatus;
  dueDate: Date;
  accessCodeQueryId: string;
  accessCode?: string;
}

export interface NormalizedLeadInterface {
  id: string;
  desiredMoveInDate: Date;
  kind: string;
  externalUnitId: string;
  phone?: string;
}

export interface RentalLead {
  [VarKey.TenantId]?: string;
  [VarKey.UserFirstName]: string;
  [VarKey.UserLastName]: string;
  [VarKey.UserAddressLine1]: string;
  [VarKey.UserAddressLine2]: string;
  [VarKey.UserCity]: string;
  [VarKey.UserState]: string;
  [VarKey.UserPostalCode]: string;
  [VarKey.UserCountryCode]?: string;
  [VarKey.UserPhoneNumber]: string;
  [VarKey.UserEmail]: string;
  notes?: string;
  /** Optional ISO date (YYYY-MM-DD) for Lease/ExpectedMoveInDate and CustomerPreferences/TargetMoveInDate */
  desiredMoveInDate?: string;
  /** Optional unit marketing name for CustomerPreferences/DesiredUnit/MarketingName */
  desiredUnitName?: string;
  /** Optional floorplan code for CustomerPreferences/DesiredFloorplan */
  desiredFloorplan?: string;
}
export enum AccessCodeRequestResponseStatus {
  SUCCESS = "success",
  FAILURE = "failure",
  MISSING_PARAMS = "missing params",
  NOT_FOUND = "not found"
}

export enum AccessCodeRequestResponseMessage {
  NOT_FOUND = "Access code not found",
  NO_ACTIVE_LEDGER_WITH_CODE = "No active ledger with gate access code found"
}

export interface AccessCodeRequestResponse {
  status: AccessCodeRequestResponseStatus;
  accessCode?: string;
  temporary?: boolean;
  message?: string;
}

export interface BaseGetParams {
  pagination?: {
    page: number;
    size: number;
  };
  sort?: {
    field: string;
    order: "asc" | "desc";
  };
  usePaginationStrategy?: boolean;
  updatedSince?: string;
}

export interface GetSpacesParams {
  priceField?: string;
  includeTax?: boolean;
}

export interface GetUnitsParams extends BaseGetParams {
  facilityId?: string;
  priceField?: string;
  includeTax?: boolean;
  unitIds?: string[];
}

export enum GetTenantResponseStatus {
  SUCCESS = "success",
  FAILURE = "failure",
  MISSING_PARAMS = "missing params",
  MULTIPLE_TENANTS = "multiple tenants",
  NOT_FOUND = "not found"
}
export interface GetTenantResponse {
  status: GetTenantResponseStatus;
  tenant?: NormalizedFMSTenantInterface;
  message?: string;
  tenants?: NormalizedFMSTenantInterface[];
}

export interface FMSError {
  name?: string;
  message?: string;
  status?: string;
  stack?: string;
}

export interface FMSServiceReturnData<T, R = any> {
  items?: T;
  totalCount?: number;
  raw?: R;
  error?: FMSError;
  status?: string;
}

export enum FMSServiceReturnDataStatus {
  SUCCESS = "success",
  FAILURE = "failure",
  /** ENG-3373: the Outbound Gate withheld the write; `error.message` names its reason. */
  SIMULATED = "simulated"
}

export interface ExtendedGetSpacesParams extends GetSpacesParams {
  full?: boolean;
}

export interface GetTenantParams {
  tenantId?: string;
  email?: string;
  phone?: string;
  facilityId?: string;
  full?: boolean;
  botId?: string;
}

/**
 * SSMCloud-specific (ENG-1691): cross-page accounting for the per-tenant SMS opt-in fetch.
 * Created once per sync run by the sync strategy and threaded through every getTenants page,
 * so the probe/fail-fast, circuit breaker, and STRICT watermark decision span the whole run.
 */
export interface SSMCloudSmsOptInFetchState {
  probed: boolean;
  authFailure: "credentials-rejected" | "endpoint-disabled" | null;
  transientFailures: number;
  deterministicSkips: number;
  resolved: number;
  totalTenants: number;
  breakerTripped: boolean;
}

export interface GetTenantsParams extends BaseGetParams {
  facilityId?: string;
  full?: boolean;
  day?: string;
  botId?: string;
  tenantIds?: string[];
  /**
   * SSMCloud-specific: pre-fetched smsOptIn values keyed by externalTenantId.
   * Other FMS implementations ignore this field.
   */
  smsOptInByExternalTenantId?: Record<string, boolean>;
  /**
   * SSMCloud-specific (ENG-1691): fetch the live SMS opt-in value (`GetTenantInfo` per tenant)
   * for each returned page and apply it to the normalized tenants. Reuses the TenantIds already
   * in hand — no separate enumeration request. Other FMS implementations ignore this field.
   */
  withSmsOptIn?: boolean;
  /**
   * SSMCloud-specific (ENG-1691): optional cross-page fetch accounting for `withSmsOptIn`.
   * The sync strategy passes one instance for the whole run to drive its watermark decision;
   * standalone callers can omit it (a throwaway per-page state is used).
   */
  smsOptInFetchState?: SSMCloudSmsOptInFetchState;
  /**
   * ENG-1555: externalTenantIds that have at least one active lease, pre-collected by the
   * caller (sync strategies pre-fetch leases once per run and inject this for every tenant
   * page). Used by FMSs whose leases live on a separate endpoint (StorEdge, SSMCloud,
   * QuikStor, Tenant) to derive `tenant.active` without an extra lease request.
   * When absent, those FMSs fetch their own leases internally.
   */
  activeExternalTenantIds?: string[];
}

export interface GetActiveTenantsResponse {
  status: GetTenantResponseStatus;
  tenants?: TenantInterface[];
  message?: string;
  errors?: string[];
}

export interface GetLeasesParams extends BaseGetParams {
  facilityId?: string;
  externalTenantId?: string;
  startDate?: string;
  endDate?: string;
  orgId?: number;
  locationId?: number;
  locationTimezone?: string;
}

export interface GetLeaseParams {
  externalLeaseId: string;
  facilityId?: string;
  externalTenantId?: string;
  orgId?: number;
  locationId?: number;
  locationTimezone?: string;
}

export interface GetLeadsParams extends BaseGetParams {
  facilityId?: string;
  startDate?: string;
  externalTenantId?: string;
}

export interface GetLeadParams {
  externalLeadId: string;
  facilityId?: string;
  externalTenantId?: string;
  orgId?: number;
  locationId?: number;
  /** Date floor for providers whose single-lead lookup is list-based (e.g. SSMCloud); by-id providers ignore it. */
  startDate?: string;
}

export interface GetLeasesSearchParams extends GetLeasesParams {
  accountNumber?: string;
}

export interface GetAccessCodeParams {
  /** Tenant/account-scoped query key (e.g. StorEdge account_number) used to LOOK UP the tenant's codes. */
  id: string;
  facilityId?: string;
  /**
   * Optional per-lease/per-unit selectors used to pick THIS lease's code out of a
   * tenant-scoped result that carries one code per unit (TKT-M / ENG-2536). Without
   * them, list-based adapters fall back to legacy first-code behavior. Each adapter
   * matches on whichever key its source data is keyed by:
   *   - StorEdge: `externalLeaseId` ↔ ledger `id`
   *   - Hummingbird: `externalUnitId` ↔ access entry `unit_id`
   */
  externalLeaseId?: string;
  externalUnitId?: string;
}

/** Why a by-lease access-code lookup returned no code. Every miss is logged. */
export type LeaseAccessCodeMissReason =
  | "no_query_id" // lease has no accessCodeQueryId synced
  | "no_fms" // lease's location has no FMS integration configured
  | "fms_mismatch" // lease was synced under a different FMS than the location's current one (pre-migration row)
  | "no_facility_id" // FMS requires a facility scope and the lease has none synced
  | "fms_error" // FMS call failed or returned no code
  | "timeout"; // FMS call exceeded the latency budget

/** Result of `FMSService.getAccessCodeForLease` — live lookup only, no cached fallback. */
export interface LeaseAccessCodeResult {
  code: string | null;
  reason: "ok" | LeaseAccessCodeMissReason;
}

export interface AppendTenantNoteParams {
  tenant: TenantInterface;
  facilityId?: string;
  orgId?: number;
}

export interface AppendTenantNoteResponse {
  isSuccess: boolean;
  externalNoteId?: string;
  responseMessage?: string;
  /** ENG-3373: the Outbound Gate withheld the write — nothing reached the FMS. `isSuccess` is false. */
  simulated?: boolean;
  /** The gate's reason when simulated (`flag_off`, `not_listed`, …). */
  gateReason?: string;
}

export enum FMSCreateLeadStatus {
  SUCCESS = "success",
  FAILURE = "failure",
  DUPLICATE = "duplicate"
}
export interface FMSCreateLeadResponse {
  status: FMSCreateLeadStatus;
  tenantId?: string;
  error?: string;
}

export interface FMSLocationData {
  orgId: number;
  locationId: number;
  fmsId: string;
  storeId: string;
  token: any;
  type: FMSType;
  isSyncEnabled?: boolean;
}

export interface FMSNote {
  title?: string;
  content: string;
  pinned?: boolean;
  archived?: boolean;
}

export enum FMSRoute {
  DATABASE = "database",
  LIVE = "live"
}

export enum FMSLeadAddToAbandonedCartSequenceStrategy {
  NONE = "",
  ORIGINATION_IS_LEAD = "ORIGINATION_IS_LEAD"
}

export type FMSPaginatedItemsResponse<R = unknown> = {
  results: R[];
  totalCount: number;
};

export function staticImplements<T>() {
  return <U extends T>(constructor: U) => {
    constructor;
  };
}
// export async function HummingbirdCreateLead(auth: HummingbirdAuthentication, lead: RentalLead, facility: FacilityInterface): Promise<FMSServiceReturnData<FMSCreateLeadResponse>> {

export interface GetEntityDependenciesParams {
  rawLeases?: any[];
  rawLeads?: any[];
}

export interface NormalizedEntityDependencies {
  tenants: NormalizedFMSTenantInterface[];
  units: NormalizedFMSUnitInterface[];
}

export interface FMSServiceInterface {
  new (): any;
  // getStoreInfo( auth:FMSValidAuth,  facilityId:string):Promise<FacilityInterface>
  createLead(auth: FMSValidAuth, lead: Partial<RentalLead>, facility: FacilityInterface): Promise<FMSServiceReturnData<FMSCreateLeadResponse>>;
  // getSpaces(auth: FMSValidAuth, facilityId: string, params?: GetSpacesParams): Promise<FMSServiceReturnData<SpaceInterface[]>>;
  getTenant(auth: FMSValidAuth, params: GetTenantParams): Promise<FMSServiceReturnData<GetTenantResponse>>;
  // getActiveTenants(auth: FMSValidAuth, params: GetActiveTenantsParams): Promise<FMSServiceReturnData<GetTenantResponse>>;
  getAccessCode(auth: FMSValidAuth, params: GetAccessCodeParams): Promise<FMSServiceReturnData<AccessCodeRequestResponse>>;

  // Mono methods - we'll eventually migrate over to these permanently
  getUnits(auth: FMSValidAuth, params: GetUnitsParams): Promise<FMSServiceReturnData<NormalizedFMSUnitInterface[]>>;
  getTenants(auth: FMSValidAuth, params: GetTenantsParams): Promise<FMSServiceReturnData<NormalizedFMSTenantInterface[]>>;
  getLeases(auth: FMSValidAuth, params: GetLeasesParams): Promise<FMSServiceReturnData<NormalizedFMSLeaseInterface[]>>;
  getLease(auth: FMSValidAuth, params: GetLeaseParams): Promise<FMSServiceReturnData<NormalizedFMSLeaseInterface>>;
  getLeads(auth: FMSValidAuth, params: GetLeadsParams): Promise<FMSServiceReturnData<NormalizedFMSLeadInterface[]>>;
  getLead(auth: FMSValidAuth, params: GetLeadParams): Promise<FMSServiceReturnData<NormalizedFMSLeadInterface>>;
  makePayments(auth: FMSValidAuth, payment: FMSPaymentInterface, facilityId: string): Promise<FMSServiceReturnData<NormalizedMakePaymentResponse>>;
  appendTenantNotes(auth: FMSValidAuth, note: FMSNote, params: AppendTenantNoteParams): Promise<AppendTenantNoteResponse>;
  getEntityDependencies?(auth: FMSValidAuth, params: GetEntityDependenciesParams): NormalizedEntityDependencies;
}
